/**
 * Maintenance Mode Control Page
 *
 * Admin page for enabling/disabling platform maintenance mode.
 * When enabled, all non-admin users see a maintenance page.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, MaintenanceStatus } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export function MaintenanceControl() {
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.adminGetMaintenanceStatus();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load maintenance status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleEnable = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const data = await api.adminSetMaintenanceMode({
        enabled: true,
        message: message.trim() || undefined,
      });
      setStatus(data);
      setMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable maintenance mode');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const data = await api.adminSetMaintenanceMode({ enabled: false });
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable maintenance mode');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Maintenance Mode</h1>
        <p className="mt-2 text-muted-foreground">
          Emergency kill switch for the platform. When enabled, all non-admin users see a
          maintenance page.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Toggling takes up to ~2 seconds to take effect across all API tasks.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-3">
              <div className="h-6 bg-muted rounded w-40" />
              <div className="h-4 bg-muted rounded w-64" />
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Current Status */}
          <Card className={status?.enabled ? 'border-red-500/50' : 'border-green-500/50'}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-3">
                    <span
                      className={`inline-flex h-3 w-3 rounded-full ${
                        status?.enabled ? 'bg-red-500 animate-pulse' : 'bg-green-500'
                      }`}
                    />
                    {status?.enabled ? 'Maintenance Mode ACTIVE' : 'Platform Online'}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {status?.enabled
                      ? `Enabled by ${status.enabledBy || 'unknown'} at ${status.enabledAt ? new Date(status.enabledAt).toLocaleString() : 'unknown time'}`
                      : 'All users can access the platform normally'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            {status?.enabled && status.message && (
              <CardContent className="pt-0">
                <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                  <span className="font-medium">Message shown to users:</span> {status.message}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Actions */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!status?.enabled ? (
                <>
                  <div>
                    <label
                      htmlFor="maintenance-message"
                      className="block text-sm font-medium text-foreground mb-1"
                    >
                      Message for users (optional)
                    </label>
                    <textarea
                      id="maintenance-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="We are performing scheduled maintenance. Please check back shortly."
                      rows={2}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={actionLoading} className="w-full">
                        {actionLoading ? 'Processing...' : 'Enable Maintenance Mode'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Enable Maintenance Mode?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will immediately block all non-admin users from accessing the
                          platform. They will see a maintenance page until you disable it.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={handleEnable}
                        >
                          Enable Maintenance Mode
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <Button onClick={handleDisable} disabled={actionLoading} className="w-full">
                  {actionLoading
                    ? 'Processing...'
                    : 'Disable Maintenance Mode — Bring Site Back Online'}
                </Button>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
